import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export const authCodeFlowConfig: AuthConfig = {
  issuer: environment.auth.issuer,
  redirectUri: environment.auth.redirectUri,
  clientId: environment.auth.clientId,
  responseType: environment.auth.responseType,
  scope: environment.auth.scope,
  showDebugInformation: environment.auth.showDebugInformation,
  // Prefer refresh_token grant instead of iframe silent refresh to avoid nonce/state warnings
  silentRefreshRedirectUri: undefined,
  useSilentRefresh: false,
  customQueryParams: {
    audience: environment.auth.audience,
  },
};

const SITE_ACCESS_GRANTED_KEY = 'siteAccessGranted';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private startingLogin = false;

  constructor(
    private oauth: OAuthService,
    private router: Router,
    private http: HttpClient,
  ) {
    this.oauth.configure(authCodeFlowConfig);
    this.oauth.loadDiscoveryDocumentAndTryLogin();
    // Use refresh tokens if available (Auth0 allows rotating refresh tokens). This avoids nonce mismatch.
    this.oauth.setupAutomaticSilentRefresh({ disableIFrame: true });

    // After tokens are received/refreshed, navigate back to intended URL if present
    this.oauth.events.subscribe(e => {
      const t = (e && (e as any).type) || '';
      if (t === 'token_received' || t === 'token_refreshed') {
        this.navigateToPostLoginTarget();
      }
    });
  }

  /** All Login / Sign-up CTAs should use this (or login/signup wrappers). */
  startLogin(): void {
    if (this.isLoggedIn() || this.startingLogin) {
      return;
    }
    this.startingLogin = true;
    this.http.get<{ publicAccessEnabled?: boolean }>(`${environment.apiBaseUrl}/public/access-policy`).subscribe({
      next: (policy) => {
        this.startingLogin = false;
        const publicOn = !!policy?.publicAccessEnabled;
        if (publicOn || this.hasSiteAccessGrant()) {
          this.oauth.initLoginFlow();
        } else {
          this.router.navigateByUrl('/access-gate');
        }
      },
      error: () => {
        // Fail open so existing open deployments stay usable if policy API is temporarily down.
        this.startingLogin = false;
        this.oauth.initLoginFlow();
      }
    });
  }

  login(): void {
    this.startLogin();
  }

  signup(): void {
    this.startLogin();
  }

  /** Call after gate verify succeeds (sessionStorage already set). */
  continueAfterGate(): void {
    this.oauth.initLoginFlow();
  }

  hasSiteAccessGrant(): boolean {
    try {
      return sessionStorage.getItem(SITE_ACCESS_GRANTED_KEY) === '1';
    } catch {
      return false;
    }
  }

  markSiteAccessGranted(): void {
    try {
      sessionStorage.setItem(SITE_ACCESS_GRANTED_KEY, '1');
    } catch {}
  }

  clearSiteAccessGrant(): void {
    try {
      sessionStorage.removeItem(SITE_ACCESS_GRANTED_KEY);
    } catch {}
  }

  logOut(): void {
    // Keep sessionStorage siteAccessGranted for this tab so re-login skips the gate
    // until the tab is closed (per public_access_design acceptance criteria).
    this.oauth.logOut();
  }

  getAccessToken(): string {
    return this.oauth.getAccessToken();
  }

  isLoggedIn(): boolean {
    return this.oauth.hasValidAccessToken();
  }

  private navigateToPostLoginTarget(): void {
    try {
      const target = localStorage.getItem('postLoginRedirect');
      if (target) {
        localStorage.removeItem('postLoginRedirect');
        // Prefer Angular navigation for SPA route
        if (target.startsWith('/')) {
          this.router.navigateByUrl(target);
        } else {
          // Fallback in case an absolute URL was stored
          window.location.href = target;
        }
      }
    } catch {}
  }
}
